import { useQueries } from '@tanstack/react-query'
import { characterSheetQuery, useCharacterSheet } from '../queries'
import type { CharacterSheet } from '../types'
import { orderedShapes, otherShapeIds, type ShapeSheet } from './printModel'

export interface PrintableSheet {
  sheet: CharacterSheet | undefined
  /** Every shape other than the one `sheet` is scoped to, in the character's shape order. */
  otherShapes: ShapeSheet[]
  isPending: boolean
  isError: boolean
  error: unknown
}

/**
 * Everything the printed sheet needs, in one hook.
 *
 * A sheet read is scoped to one shape (`?shapeId=`), which is right for a screen that shows
 * one shape at a time and wrong for a page that prints them all: a shapeshifter's skills,
 * stress, consequences and powers are all shape-scoped, so a complete printed sheet needs one
 * read per shape.
 *
 * So this loads the default sheet first, reads the shape list off it, and then asks for the
 * rest through `useQueries` — a fan-out rather than a hook in a loop. Every read goes through
 * the same query options the screen sheet uses, so opening the print route from a sheet that
 * is already on screen costs nothing for the shape it was showing.
 *
 * `isPending` stays true until every shape has landed. A page that printed while half its
 * shapes were still loading would come out of the printer missing a block, and unlike a
 * screen there is no second render to fix it.
 */
export function usePrintableSheet(characterId: number): PrintableSheet {
  const base = useCharacterSheet(characterId, null)

  const shapes = orderedShapes(base.data?.sections.shapes?.shapes ?? [])
  const pending = otherShapeIds(shapes, base.data?.shapeId ?? null)

  const others = useQueries({
    queries: pending.map((shapeId) => characterSheetQuery(characterId, shapeId)),
  })

  const otherShapes: ShapeSheet[] = []
  for (const [index, shapeId] of pending.entries()) {
    const result = others[index]
    const shape = shapes.find((candidate) => candidate.id === shapeId)
    if (result?.data === undefined || shape === undefined) continue
    otherShapes.push({ shape, sheet: result.data })
  }

  const failed = others.find((result) => result.isError)

  return {
    sheet: base.data,
    otherShapes,
    isPending: base.isPending || others.some((result) => result.isPending),
    isError: base.isError || failed !== undefined,
    error: base.error ?? failed?.error ?? null,
  }
}
